import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { listReminders, createReminder, snoozeReminder, completeReminder, dismissReminder } from '../api/client';
import { ReminderCard, EmptyState } from '../components';
import type { Reminder } from '../components';

type TabKey = 'overdue' | 'today' | 'upcoming' | 'completed';

function groupReminders(reminders: Reminder[]) {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const todayEnd = new Date(now);
  todayEnd.setHours(23, 59, 59, 999);

  const groups: Record<TabKey, Reminder[]> = { overdue: [], today: [], upcoming: [], completed: [] };

  for (const r of reminders) {
    if (r.status === 'completed' || r.status === 'dismissed') {
      groups.completed.push(r);
      continue;
    }
    const due = new Date(r.due_date);
    if (due < now) groups.overdue.push(r);
    else if (due <= todayEnd) groups.today.push(r);
    else groups.upcoming.push(r);
  }

  for (const key of Object.keys(groups) as TabKey[]) {
    groups[key].sort((a, b) => new Date(a.due_date).getTime() - new Date(b.due_date).getTime());
  }
  return groups;
}

export default function Reminders() {
  const { t } = useTranslation();
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabKey>('today');
  const [showCreate, setShowCreate] = useState(false);
  const [snoozeTarget, setSnoozeTarget] = useState<string | null>(null);
  const [snoozeDate, setSnoozeDate] = useState('');

  // Create form state
  const [form, setForm] = useState({ title: '', description: '', due_date: '' });

  const fetchReminders = useCallback(async () => {
    try {
      const res = await listReminders();
      setReminders(res.data?.reminders ?? res.data ?? []);
    } catch {
      setReminders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchReminders(); }, [fetchReminders]);

  const groups = groupReminders(reminders);

  const handleComplete = async (id: string) => {
    await completeReminder(id);
    fetchReminders();
  };

  const handleDismiss = async (id: string) => {
    await dismissReminder(id);
    fetchReminders();
  };

  const handleSnoozeSubmit = async () => {
    if (!snoozeTarget || !snoozeDate) return;
    await snoozeReminder(snoozeTarget, { snoozed_until: snoozeDate });
    setSnoozeTarget(null);
    setSnoozeDate('');
    fetchReminders();
  };

  const handleReschedule = (id: string) => {
    setSnoozeTarget(id);
  };

  const handleCreate = async () => {
    if (!form.title || !form.due_date) return;
    await createReminder({ title: form.title, description: form.description || undefined, due_date: form.due_date, source: 'manual' });
    setForm({ title: '', description: '', due_date: '' });
    setShowCreate(false);
    fetchReminders();
  };

  const tabs: { key: TabKey; color: string }[] = [
    { key: 'overdue', color: 'bg-red-500' },
    { key: 'today', color: 'bg-orange-500' },
    { key: 'upcoming', color: 'bg-blue-500' },
    { key: 'completed', color: 'bg-gray-400' },
  ];

  if (loading) {
    return <div className="flex items-center justify-center min-h-[40vh] text-gray-400">{t('common.loading')}</div>;
  }

  const totalCount = reminders.length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('reminders.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('reminders.subtitle')}</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 transition-colors"
        >
          + {t('reminders.createManual')}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {tabs.map(({ key, color }) => {
          const count = groups[key].length;
          return (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`relative px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === key
                  ? 'text-primary-700 border-b-2 border-primary-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t(`common.${key}`)}
              {count > 0 && (
                <span className={`ml-2 inline-flex items-center justify-center rounded-full ${color} text-white text-[10px] font-bold min-w-[18px] h-[18px] px-1`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Reminder list for active tab */}
      {groups[activeTab].length === 0 ? (
        <EmptyState
          icon={activeTab === 'completed' ? '✅' : '⏰'}
          title={activeTab === 'completed' ? t('common.completed') : t(`common.${activeTab}`)}
          description={totalCount === 0 ? t('reminders.emptyState') : t('common.noResults')}
          action={totalCount === 0 ? { label: t('reminders.createManual'), onClick: () => setShowCreate(true) } : undefined}
        />
      ) : (
        <div className="grid gap-3">
          {groups[activeTab].map((reminder) => (
            <ReminderCard
              key={reminder.id}
              reminder={reminder}
              onSnooze={(id) => { setSnoozeTarget(id); setSnoozeDate(''); }}
              onComplete={handleComplete}
              onDismiss={handleDismiss}
              onReschedule={handleReschedule}
            />
          ))}
        </div>
      )}

      {/* Snooze / Reschedule date picker modal */}
      {snoozeTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setSnoozeTarget(null)}>
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('reminders.reschedule')}</h3>
            <input
              type="datetime-local"
              value={snoozeDate}
              onChange={(e) => setSnoozeDate(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none mb-4"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setSnoozeTarget(null)} className="rounded-md px-4 py-2 text-sm text-gray-600 hover:bg-gray-100">
                {t('common.cancel')}
              </button>
              <button onClick={handleSnoozeSubmit} disabled={!snoozeDate} className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50">
                {t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Reminder modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('reminders.createManual')}</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                  placeholder="Follow up on order..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('reminders.dueDate')}</label>
                <input
                  type="datetime-local"
                  value={form.due_date}
                  onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowCreate(false)} className="rounded-md px-4 py-2 text-sm text-gray-600 hover:bg-gray-100">
                {t('common.cancel')}
              </button>
              <button onClick={handleCreate} disabled={!form.title || !form.due_date} className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50">
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
